import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { NavLink } from "@/components/NavLink";
import { useAuth } from "@/hooks/useAuth";
import { Menu, Briefcase, LogOut, LayoutDashboard, Bell, MessageCircle, Shield, MessageSquare } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import logo from "@/assets/logo-chillwerk.png";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useUnreadTeamMessages } from "@/hooks/useUnreadTeamMessages";
import { StartChoiceDialog } from "@/components/StartChoiceDialog";

export const Navigation = () => {
  const { isEnabled: showJobsLinks } = useFeatureFlag('show_jobs_links');
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [startDialogOpen, setStartDialogOpen] = useState(false);

  // Check if current route is a dashboard route
  const isDashboardRoute = location.pathname.startsWith('/dashboard/') || 
                           location.pathname.startsWith('/kandidaat/') || 
                           location.pathname.startsWith('/bedrijf/') ||
                           location.pathname.startsWith('/admin/') ||
                           location.pathname.startsWith('/feed') ||
                           location.pathname.startsWith('/social') ||
                           location.pathname.startsWith('/channel') ||
                           location.pathname.startsWith('/notificaties') ||
                           location.pathname.startsWith('/support') ||
                           location.pathname === '/account-settings';

  // Fetch user profile to determine dashboard link and display name
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from('profiles')
        .select('user_type, full_name')
        .eq('id', user.id)
        .single();
      return data;
    },
    enabled: !!user?.id
  });

  // Fetch company name if user is a company
  const { data: companyData } = useQuery({
    queryKey: ['company-name', user?.id],
    queryFn: async () => {
      if (!user?.id || profile?.user_type !== 'company') return null;
      const { data } = await supabase
        .from('companies')
        .select('name')
        .eq('user_id', user.id)
        .single();
      return data;
    },
    enabled: !!user?.id && profile?.user_type === 'company'
  });

  // Determine display name
  const displayName = profile?.user_type === 'company' 
    ? (companyData?.name || profile?.full_name || 'Bedrijf')
    : (profile?.full_name || 'Gebruiker');

  // Fetch unread notifications count
  const { data: unreadNotifications = 0, refetch: refetchNotifications } = useQuery({
    queryKey: ['unread-notifications', user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;
      const { count } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('read', false);
      return count || 0;
    },
    enabled: !!user?.id
  });

  // Fetch unread messages count - updated to support matches without jobs
  const { data: unreadMessages = 0, refetch: refetchMessages } = useQuery({
    queryKey: ['unread-messages', user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;

      // For candidates: get matches where they are from_user_id OR to_user_id (company_to_candidate)
      // For companies: get matches where they are from_user_id OR their jobs received applications
      
      // Get matches where user initiated
      const { data: userInitiatedMatches } = await supabase
        .from('matches')
        .select('id')
        .eq('from_user_id', user.id);

      // Get matches where user is the target (company_to_candidate)
      const { data: targetedMatches } = await supabase
        .from('matches')
        .select('id')
        .eq('to_user_id', user.id);

      // Get company ID if user is a company
      const { data: companyData } = await supabase
        .from('companies')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      let companyJobMatches: { id: string }[] = [];
      if (companyData?.id) {
        // Get matches for company's jobs
        const { data: jobMatches } = await supabase
          .from('matches')
          .select('id, job:jobs!inner(company_id)')
          .eq('job.company_id', companyData.id);
        
        companyJobMatches = jobMatches || [];
      }

      // Combine all match IDs
      const allMatchIds = [
        ...(userInitiatedMatches || []).map(m => m.id),
        ...(targetedMatches || []).map(m => m.id),
        ...companyJobMatches.map(m => m.id)
      ];

      // Remove duplicates
      const uniqueMatchIds = [...new Set(allMatchIds)];

      if (uniqueMatchIds.length === 0) return 0;

      // Count unread messages in these matches. Batched, because companies can
      // have so many matches that a single request URL gets too long (HTTP 400).
      const BATCH = 150;
      let total = 0;
      for (let i = 0; i < uniqueMatchIds.length; i += BATCH) {
        const { count } = await supabase
          .from('messages')
          .select('id', { count: 'exact', head: true })
          .in('match_id', uniqueMatchIds.slice(i, i + BATCH))
          .eq('read', false)
          .neq('sender_id', user.id);
        total += count || 0;
      }

      return total;
    },
    enabled: !!user?.id
  });

  // Fetch unread support ticket replies count
  const { data: unreadSupportReplies = 0 } = useQuery({
    queryKey: ['unread-support-replies', user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;
      
      // Get tickets for this user
      const { data: tickets } = await supabase
        .from('support_tickets')
        .select('id')
        .eq('user_id', user.id);

      if (!tickets || tickets.length === 0) return 0;

      const ticketIds = tickets.map(t => t.id);

      // Count admin replies that are newer than the ticket's last update
      const { count } = await supabase
        .from('support_ticket_replies')
        .select('*', { count: 'exact', head: true })
        .in('ticket_id', ticketIds)
        .eq('is_admin_reply', true);

      return count || 0;
    },
    enabled: !!user?.id
  });

  // Fetch unread team messages count
  const { data: unreadTeamMessages = 0 } = useUnreadTeamMessages();

  // Check if user is admin
  const { data: isAdmin = false } = useQuery({
    queryKey: ['is-admin', user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();
      return !!data;
    },
    enabled: !!user?.id
  });

  // Total unread message count (match messages + team messages)
  const totalUnreadMessages = (unreadMessages ?? 0) + (unreadTeamMessages ?? 0);

  // Subscribe to real-time notifications
  useEffect(() => {
    if (!user?.id) return;

    const notificationsChannel = supabase
      .channel('nav-notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          refetchNotifications();
        }
      )
      .subscribe();

    const messagesChannel = supabase
      .channel('nav-messages')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages'
        },
        () => {
          refetchMessages();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(notificationsChannel);
      supabase.removeChannel(messagesChannel);
    };
  }, [user?.id, refetchNotifications, refetchMessages]);

  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleAuthClick = () => {
    if (user) {
      signOut();
    } else {
      navigate("/auth");
    }
  };

  return (
    <nav
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-offwhite/80 backdrop-blur-[10px] border-b border-border/30 shadow-[0_2px_16px_-6px_rgba(0,0,0,0.15)]'
          : 'bg-offwhite border-b border-border/20 shadow-[0_4px_30px_-6px_rgba(0,0,0,0.12),0_1px_3px_-1px_rgba(0,0,0,0.06)]'
      }`}
    >
      <div className="container mx-auto px-3 sm:px-4 max-w-7xl">
        <div className={`flex items-center justify-between transition-all duration-300 ${scrolled ? 'h-14 md:h-15 lg:h-16' : 'h-16 md:h-18 lg:h-20'}`}>
          {/* Logo */}
          <NavLink to="/" className="flex items-center gap-3 transition-all duration-500 hover:scale-105 group">
            <div className="relative">
              <img src={logo} alt="Chillwerk — de nieuwe generatie werk" className={`object-contain drop-shadow-[0_2px_8px_rgba(114,208,243,0.4)] group-hover:drop-shadow-[0_4px_16px_rgba(114,208,243,0.6)] transition-all duration-500 ${scrolled ? 'h-10 w-10 md:h-11 md:w-11 lg:h-12 lg:w-12' : 'h-12 w-12 md:h-13 md:w-13 lg:h-14 lg:w-14'}`} />
              <div className="absolute inset-0 rounded-full bg-cyan/10 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 scale-150" />
            </div>
          </NavLink>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-4 lg:gap-6">
            {showJobsLinks && (
              <NavLink to="/vacatures" className="text-foreground/80 hover:text-foreground font-medium transition-all duration-300 tracking-wide">
                Vacatures
              </NavLink>
            )}
          </div>
          
          {/* Right Actions */}
          <div className="flex items-center gap-3 md:gap-4 lg:gap-6">
            {user ? (
              <>
                {isAdmin && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="hidden lg:inline-flex gap-2 border-primary/50"
                    onClick={() => navigate('/admin')}
                  >
                    <Shield className="w-4 h-4" />
                    Admin
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative md:w-9 md:h-9 lg:w-10 lg:h-10"
                  onClick={() => {
                    const messagesPath = profile?.user_type === 'company' 
                      ? '/bedrijf/berichten' 
                      : '/kandidaat/berichten';
                    navigate(messagesPath);
                  }}
                  aria-label="Berichten"
                >
                  <MessageCircle className="w-4 h-4 md:w-5 md:h-5" />
                  {totalUnreadMessages > 0 && (
                    <Badge 
                      variant="destructive" 
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                    >
                      {totalUnreadMessages > 9 ? '9+' : totalUnreadMessages}
                    </Badge>
                  )}
                </Button>
                <Button 
                  variant="ghost"
                  size="icon"
                  className="relative md:w-9 md:h-9 lg:w-10 lg:h-10"
                  onClick={() => navigate('/notificaties')}
                  aria-label="Notificaties"
                >
                  <Bell className="w-4 h-4 md:w-5 md:h-5" />
                  {unreadNotifications > 0 && (
                    <Badge 
                      variant="destructive" 
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                    >
                      {unreadNotifications > 9 ? '9+' : unreadNotifications}
                    </Badge>
                  )}
                </Button>
                <Button 
                  variant="ghost" 
                  className="hidden lg:inline-flex px-3 lg:px-6 gap-2"
                  onClick={() => {
                    const dashboardPath = profile?.user_type === 'company' 
                      ? '/dashboard/bedrijf' 
                      : '/dashboard/kandidaat';
                    navigate(dashboardPath);
                  }}
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Dashboard
                </Button>
                <Button 
                  variant="ghost" 
                  className="hidden lg:inline-flex px-3 md:px-4 lg:px-6 gap-1 md:gap-2 relative"
                  onClick={() => navigate('/support')}
                >
                  <MessageSquare className="w-4 h-4" />
                  Support
                  {unreadSupportReplies > 0 && (
                    <Badge 
                      variant="destructive" 
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                    >
                      {unreadSupportReplies}
                    </Badge>
                  )}
                </Button>
                <Button
                  variant="ghost" 
                  className="hidden lg:inline-flex px-3 lg:px-6 gap-2"
                  onClick={handleAuthClick}
                >
                  <LogOut className="w-4 h-4" />
                  Uitloggen
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  className="hidden lg:inline-flex text-base font-semibold hover:bg-foreground/5 transition-all duration-300"
                  onClick={handleAuthClick}
                >
                  Inloggen
                </Button>
                <Button 
                  variant="cta" 
                  className="hidden sm:inline-flex text-sm md:text-base font-bold shadow-[0_4px_20px_hsl(var(--secondary)/0.4)] hover:shadow-[0_6px_30px_hsl(var(--secondary)/0.6)] hover:scale-105 transition-all duration-300 border-2 border-secondary/50"
                  onClick={() => setStartDialogOpen(true)}
                >
                  Start nu
                </Button>
                <StartChoiceDialog open={startDialogOpen} onOpenChange={setStartDialogOpen} />
              </>
            )}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden max-md:h-11 max-md:w-11" aria-label="Open menu">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <SheetHeader>
                  <SheetTitle className="text-left">
                    <img src={logo} alt="Chillwerk — de nieuwe generatie werk" className="h-14 w-14 object-contain mb-4" />
                  </SheetTitle>
                </SheetHeader>
                <div className="flex flex-col gap-4 mt-6">
                  {showJobsLinks && (
                    <NavLink 
                      to="/vacatures" 
                      className="text-foreground hover:text-cyan-dark font-medium transition-colors py-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Vacatures
                    </NavLink>
                  )}
                  
                  {user ? (
                    <>
                      <div className="border-t border-border pt-4 mt-2">
                        {isAdmin && (
                          <Button 
                            variant="outline" 
                            className="w-full justify-start gap-2 border-primary/50 mb-2"
                            onClick={() => {
                              navigate('/admin');
                              setMobileMenuOpen(false);
                            }}
                          >
                            <Shield className="w-4 h-4" />
                            Admin Panel
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          className="w-full justify-start gap-2"
                          onClick={() => {
                            const dashboardPath = profile?.user_type === 'company' 
                              ? '/dashboard/bedrijf' 
                              : '/dashboard/kandidaat';
                            navigate(dashboardPath);
                            setMobileMenuOpen(false);
                          }}
                        >
                          <LayoutDashboard className="w-4 h-4" />
                          Dashboard
                        </Button>
                        <Button 
                          variant="ghost" 
                          className="w-full justify-start gap-2 relative"
                          onClick={() => {
                            const messagesPath = profile?.user_type === 'company' 
                              ? '/bedrijf/berichten' 
                              : '/kandidaat/berichten';
                            navigate(messagesPath);
                            setMobileMenuOpen(false);
                          }}
                        >
                          <MessageCircle className="w-4 h-4" />
                          Berichten
                          {totalUnreadMessages > 0 && (
                            <Badge 
                              variant="destructive" 
                              className="ml-auto h-5 w-5 flex items-center justify-center p-0 text-xs"
                            >
                              {totalUnreadMessages > 9 ? '9+' : totalUnreadMessages}
                            </Badge>
                          )}
                        </Button>
                        <Button 
                          variant="ghost" 
                          className="w-full justify-start gap-2 relative"
                          onClick={() => {
                            navigate('/notificaties');
                            setMobileMenuOpen(false);
                          }}
                        >
                          <Bell className="w-4 h-4" />
                          Notificaties
                          {unreadNotifications > 0 && (
                            <Badge 
                              variant="destructive" 
                              className="ml-auto h-5 w-5 flex items-center justify-center p-0 text-xs"
                            >
                              {unreadNotifications > 9 ? '9+' : unreadNotifications}
                            </Badge>
                          )}
                        </Button>
                        <Button 
                          variant="ghost" 
                          className="w-full justify-start gap-2 relative"
                          onClick={() => {
                            navigate('/support');
                            setMobileMenuOpen(false);
                          }}
                        >
                          <MessageSquare className="w-4 h-4" />
                          Support
                          {unreadSupportReplies > 0 && (
                            <Badge 
                              variant="destructive" 
                              className="ml-auto h-5 w-5 flex items-center justify-center p-0 text-xs"
                            >
                              {unreadSupportReplies}
                            </Badge>
                        )}
                      </Button>
                      <Button
                          variant="ghost" 
                          className="w-full justify-start gap-2 mt-2"
                          onClick={() => {
                            handleAuthClick();
                            setMobileMenuOpen(false);
                          }}
                        >
                          <LogOut className="w-4 h-4" />
                          Uitloggen
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="border-t border-border pt-4 mt-2 space-y-2">
                      <Button 
                        variant="ghost" 
                        className="w-full"
                        onClick={() => {
                          handleAuthClick();
                          setMobileMenuOpen(false);
                        }}
                      >
                        Inloggen
                      </Button>
                      <Button 
                        variant="cta" 
                        className="w-full"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setStartDialogOpen(true);
                        }}
                      >
                        Start nu
                      </Button>
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};
